import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Star,
  Clock,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Scissors,
  ExternalLink,
  Share2,
  QrCode,
  Home,
  ArrowLeft,
  Eye
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";

/**
 * Pro Mini-Site - Public landing page for Pro/Elite stylists
 * 
 * Features:
 * - Welcome banner with photo, name, bio
 * - Business hours
 * - Contact info
 * - Booking policies
 * - Service menu
 * - Portfolio gallery
 * - Book Now CTA
 */
const ProMiniSite = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showPolicies, setShowPolicies] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [activeSection, setActiveSection] = useState("welcome");
  
  // Check if we're in preview mode
  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';

  useEffect(() => {
    fetchMiniSiteData();
  }, [slug]);

  const fetchMiniSiteData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/minisite/public/${slug}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch mini-site:", err);
      if (err.response?.status === 404) {
        setError("Profile not found");
      } else if (err.response?.status === 403) {
        setError("This feature requires a Pro or Elite subscription");
      } else {
        setError("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Book with ${data?.name} on MyHairChair`,
          text: data?.bio || `Book your next appointment with ${data?.name}`,
          url
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const bookingUrl = data ? `/book-guest/${data.user_id}` : "#";
  const fullUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Custom branding colors (Elite feature)
  const primaryColor = data?.custom_branding?.primary_color || "#d4af37"; // Gold
  const secondaryColor = data?.custom_branding?.secondary_color || "#dc2626"; // Red
  const bgColor = data?.custom_branding?.bg_color || "#000000"; // Black

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-white/60">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-stone-900 border-stone-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Scissors className="w-16 h-16 text-stone-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Profile Not Available</h1>
            <p className="text-stone-400 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                Go to MyHairChair
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: bgColor }}
    >
      {/* Preview Mode Banner */}
      {isPreview && (
        <div className="sticky top-0 z-50 bg-amber-500 text-black py-3 px-4 shadow-lg">
          <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5" />
              <span className="font-medium">Preview Mode</span>
              <span className="text-amber-900 text-sm">This is how clients see your public booking page</span>
            </div>
            <Link to="/dashboard?tab=profile">
              <Button 
                variant="outline" 
                className="bg-black/10 border-black/30 text-black hover:bg-black/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Gold Top Bar */}
      <div 
        className="h-2"
        style={{ background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}88)` }}
      />

      {/* Back to App Button - hide in preview mode */}
      {!isPreview && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-stone-400 hover:text-white transition-colors"
            data-testid="back-to-app-btn"
          >
            <Home className="w-4 h-4" />
            <span className="text-sm">Back to MyHairChair</span>
          </Link>
        </div>
      )}

      {/* Welcome Section */}
      <section className="relative px-4 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 
              className="text-3xl md:text-5xl font-serif italic mb-2"
              style={{ color: primaryColor }}
            >
              WELCOME TO
            </h1>
          </div>

          {/* Profile Card */}
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Profile Image */}
            <div className="relative flex-shrink-0">
              <div 
                className="w-48 h-48 md:w-64 md:h-72 rounded-full md:rounded-[40%] overflow-hidden border-4"
                style={{ borderColor: primaryColor }}
              >
                {data.profile_picture ? (
                  <img 
                    src={data.profile_picture} 
                    alt={data.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-stone-800 flex items-center justify-center">
                    <Scissors className="w-16 h-16 text-stone-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h2 
                className="text-3xl md:text-4xl font-serif italic mb-4"
                style={{ color: secondaryColor }}
              >
                {data.name}
              </h2>
              
              <p className="text-white/90 text-lg mb-6 max-w-lg">
                {data.bio || `${data.experience_years || 0} years experience as a ${data.professional_type?.toLowerCase() || 'professional'}. Professional and punctual.`}
              </p>

              {/* Services Highlight */}
              {data.services?.length > 0 && (
                <div className="mb-6">
                  <p className="text-white/80 mb-2">
                    {data.services.slice(0, 2).map((s, i) => (
                      <span key={i}>
                        ${s.price} for {s.name}{i < 1 && data.services.length > 1 ? " • " : ""}
                      </span>
                    ))}
                  </p>
                </div>
              )}

              {/* Signature */}
              {data.signature_text && (
                <p 
                  className="font-script text-3xl italic"
                  style={{ color: primaryColor }}
                >
                  {data.signature_text}
                </p>
              )}

              {/* Location */}
              {(data.address || data.city) && (
                <div className="flex items-center justify-center md:justify-start gap-2 mt-4 text-white/70">
                  <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                  <span>
                    {[data.address, data.city, data.state, data.zip_code].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Book Now Button */}
          <div className="mt-8 text-center">
            <Link to={bookingUrl}>
              <Button 
                size="lg"
                className="px-12 py-6 text-xl font-bold rounded-full shadow-2xl transform hover:scale-105 transition-all"
                style={{ 
                  background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                  color: bgColor
                }}
                data-testid="book-now-btn"
              >
                <Calendar className="w-6 h-6 mr-2" />
                BOOK NOW
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Gold Divider */}
      <div 
        className="h-1 mx-8"
        style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }}
      />

      {/* Hours & Contact Section */}
      <section className="px-4 py-8 md:py-12">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Hours */}
          <div>
            <h3 
              className="text-2xl font-serif mb-1"
              style={{ color: primaryColor }}
            >
              HOURS
            </h3>
            <p 
              className="font-script text-xl italic mb-4"
              style={{ color: primaryColor, opacity: 0.7 }}
            >
              hours
            </p>
            
            <Card className="bg-stone-100 border-0">
              <CardContent className="p-6">
                <div className="space-y-2">
                  {Object.entries(data.business_hours || {}).map(([day, hours]) => (
                    <div key={day} className="flex justify-between text-sm">
                      <span className="font-semibold uppercase">{day}</span>
                      <span className="font-medium">{hours}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact */}
          <div>
            <h3 
              className="text-2xl font-serif mb-1"
              style={{ color: secondaryColor }}
            >
              CONTACT
            </h3>
            <p 
              className="font-script text-xl italic mb-4"
              style={{ color: secondaryColor, opacity: 0.7 }}
            >
              contact
            </p>
            
            <Card className="bg-stone-100 border-0">
              <CardContent className="p-6 space-y-4">
                {data.social_links?.email && (
                  <a 
                    href={`mailto:${data.social_links.email}`}
                    className="flex items-center gap-3 text-stone-800 hover:text-red-600 transition-colors"
                  >
                    <div 
                      className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}20` }}
                    >
                      <Mail className="w-5 h-5" style={{ color: secondaryColor }} />
                    </div>
                    <span className="font-medium">{data.social_links.email}</span>
                  </a>
                )}
                
                {(data.phone || data.social_links?.phone) && (
                  <a 
                    href={`tel:${data.phone || data.social_links?.phone}`}
                    className="flex items-center gap-3 text-stone-800 hover:text-red-600 transition-colors"
                  >
                    <div 
                      className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}20` }}
                    >
                      <Phone className="w-5 h-5" style={{ color: secondaryColor }} />
                    </div>
                    <span className="font-medium">{data.phone || data.social_links?.phone}</span>
                  </a>
                )}
                
                {data.social_links?.instagram && (
                  <a 
                    href={`https://instagram.com/${data.social_links.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-stone-800 hover:text-red-600 transition-colors"
                  >
                    <div 
                      className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}20` }}
                    >
                      <Instagram className="w-5 h-5" style={{ color: secondaryColor }} />
                    </div>
                    <span className="font-medium">@{data.social_links.instagram.replace('@', '')}</span>
                  </a>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gold Divider */}
      <div 
        className="h-1 mx-8"
        style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }}
      />

      {/* Booking Policies Section */}
      <section className="px-4 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setShowPolicies(!showPolicies)}
            className="w-full text-center mb-6"
          >
            <p 
              className="font-script text-2xl italic mb-2"
              style={{ color: primaryColor }}
            >
              Please read before booking
            </p>
            <div className="flex items-center justify-center gap-2 text-white/60">
              <span>View Booking Policies</span>
              {showPolicies ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>

          {showPolicies && (
            <div className="space-y-6 animate-in slide-in-from-top duration-300">
              {/* Rescheduling/Cancellations */}
              <div className="text-center">
                <h4 
                  className="text-xl font-bold mb-2"
                  style={{ color: secondaryColor }}
                >
                  RESCHEDULING/CANCELLATIONS
                </h4>
                <div 
                  className="w-32 h-0.5 mx-auto mb-3"
                  style={{ backgroundColor: primaryColor }}
                />
                <p className="text-white/80">
                  Cancellations must be done at least {data.booking_policies?.cancellation_notice || "2 hours"} before your appointment.
                </p>
              </div>

              {/* Deposits */}
              <div className="text-center">
                <h4 
                  className="text-xl font-bold mb-2"
                  style={{ color: secondaryColor }}
                >
                  DEPOSITS:
                </h4>
                <div 
                  className="w-32 h-0.5 mx-auto mb-3"
                  style={{ backgroundColor: primaryColor }}
                />
                <p className="text-white/80">
                  A non refundable deposit of ${data.booking_policies?.deposit_amount || 10} is required per client.
                  {data.booking_policies?.deposit_service_fee > 0 && (
                    <> (Plus a ${data.booking_policies?.deposit_service_fee || 2}.00 service fee)</>
                  )}
                  <br />
                  Deposits will be deducted from total balance.
                </p>
              </div>

              {/* No Shows */}
              <div className="text-center">
                <h4 
                  className="text-xl font-bold mb-2"
                  style={{ color: secondaryColor }}
                >
                  NO SHOWS:
                </h4>
                <div 
                  className="w-32 h-0.5 mx-auto mb-3"
                  style={{ backgroundColor: primaryColor }}
                />
                <p className="text-white/80">
                  If you make an appointment and do not show, or cancel before your slot,
                  you will be charged the full amount of your scheduled service.
                </p>
              </div>

              {/* Late Policy */}
              <div className="text-center">
                <h4 
                  className="text-xl font-bold mb-2"
                  style={{ color: secondaryColor }}
                >
                  LATE POLICY:
                </h4>
                <div 
                  className="w-32 h-0.5 mx-auto mb-3"
                  style={{ backgroundColor: primaryColor }}
                />
                <p className="text-white/80">
                  Be on time! There is a {data.booking_policies?.late_grace_period || 10} minute grace period.
                  After that, there will be a ${data.booking_policies?.late_fee || 10} fee.
                  After {data.booking_policies?.late_cancel_minutes || 15} minutes, your appointment will be cancelled. No exceptions.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gold Divider */}
      <div 
        className="h-1 mx-8"
        style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }}
      />

      {/* Services Section */}
      {data.services?.length > 0 && (
        <section className="px-4 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 
                className="text-2xl font-serif mb-1"
                style={{ color: primaryColor }}
              >
                SERVICES
              </h3>
              <p 
                className="font-script text-xl italic"
                style={{ color: primaryColor, opacity: 0.7 }}
              >
                Please choose your service below
              </p>
            </div>

            <div className="grid gap-4">
              {data.services.map((service, idx) => (
                <Card 
                  key={idx} 
                  className="bg-stone-900/50 border-stone-800 hover:border-amber-500/50 transition-colors"
                >
                  <CardContent className="p-4 flex justify-between items-center">
                    <div>
                      <h4 className="text-white font-semibold">{service.name}</h4>
                      {service.description && (
                        <p className="text-white/60 text-sm">{service.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-sm text-white/50">
                        {service.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {service.duration} min
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span 
                        className="text-2xl font-bold"
                        style={{ color: primaryColor }}
                      >
                        ${service.price}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Book Now */}
            <div className="mt-8 text-center">
              <Link to={bookingUrl}>
                <Button 
                  size="lg"
                  className="px-12 py-6 text-xl font-bold rounded-full"
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                    color: bgColor
                  }}
                >
                  <Calendar className="w-6 h-6 mr-2" />
                  BOOK YOUR APPOINTMENT
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gold Divider */}
      <div 
        className="h-1 mx-8"
        style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }}
      />

      {/* Portfolio Gallery Section - Max 5 Images */}
      {data.portfolio_gallery?.length > 0 && (
        <section className="px-4 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 
                className="text-2xl font-serif mb-1"
                style={{ color: primaryColor }}
              >
                MY WORK
              </h3>
              <p 
                className="font-script text-xl italic mb-2"
                style={{ color: primaryColor, opacity: 0.7 }}
              >
                portfolio
              </p>
            </div>

            {/* Gallery Grid - 5 images max */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.portfolio_gallery.slice(0, 5).map((img, idx) => (
                <div 
                  key={idx}
                  className={`aspect-square overflow-hidden rounded-xl border-2 shadow-lg ${
                    idx === 0 ? 'md:col-span-1 md:row-span-1' : ''
                  }`}
                  style={{ borderColor: primaryColor }}
                >
                  <img 
                    src={img.image_url || img}
                    alt={img.style_name || `Work sample ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>

            {/* Review CTA */}
            <div className="text-center mt-8">
              <p style={{ color: secondaryColor }} className="text-lg font-medium">
                Love my work? Leave me a review on Google!
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Footer Actions */}
      <section className="px-4 py-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              variant="outline"
              onClick={handleShare}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Profile
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowQR(!showQR)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </Button>
          </div>

          {/* QR Code Modal */}
          {showQR && (
            <div className="mt-6 flex justify-center">
              <Card className="bg-white p-4">
                <QRCode 
                  value={fullUrl}
                  size={200}
                  level="H"
                />
                <p className="text-center text-sm mt-2 text-stone-600">
                  Scan to book
                </p>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Branding Footer (for non-Elite) */}
      {data.show_branding && (
        <footer className="py-4 text-center border-t border-white/10">
          <Link 
            to="/"
            className="text-white/40 hover:text-white/60 text-sm flex items-center justify-center gap-2"
          >
            <span>Powered by</span>
            <span className="font-bold" style={{ color: primaryColor }}>MyHairChair</span>
          </Link>
        </footer>
      )}

      {/* Gold Bottom Bar */}
      <div 
        className="h-2"
        style={{ background: `linear-gradient(90deg, ${primaryColor}88, ${primaryColor})` }}
      />
    </div>
  );
};

export default ProMiniSite;
